/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import { PreviewType } from 'client-code/previews';
import { FeedItemType } from 'client-code/wrs-feed-service';

import { DimensionScaler } from '../device/DimensionScaler';

export type ImageType = 'MOVIE' | 'EPISODE' | 'SPORTS' | 'TEAM' | 'COLLECTION' | 'GALLERY_TILE';

/**
 * Reference : MdoUtils.hx -> buildImageUrlFromIdForDIGv2, getImageTypeFromCollectionType
 * ContentImageFactory.hx
 */
export class ImageUrlGenerator {
    public buildImageUrl = (
        baseUrl: string,
        id: string,
        imageType: ImageType,
        width: number = 662,
        height: number = 372,
    ): string => {
        // Here we assume that the image is type of Collection
        // therefore imageType , width , height are taken according to collection type
        const { height: scaledHeight, width: scaledWidth } =
            DimensionScaler.getInstance().getScaledDimensions(width, height);

        let imageUrl = baseUrl;
        imageUrl += `/images?objectid=${id}&imagetype=`;
        if (imageType === 'MOVIE') {
            imageUrl += `moviePoster&width=${scaledWidth}&height=${scaledHeight}`;
        } else if (imageType === 'EPISODE' || id.includes('ct') || imageType === 'SPORTS') {
            imageUrl += `episodeBanner&width=${scaledWidth}&height=${scaledHeight}`;
        } else if (imageType === 'COLLECTION' || imageType === 'GALLERY_TILE') {
            imageUrl += `showcaseBanner&width=${scaledWidth}&height=${scaledHeight}`;
        }

        if (id.includes('ct')) {
            imageUrl += '&fallbackPolicy=episodeToSeries';
        } else if (imageType === 'SPORTS') {
            imageUrl += '&fallbackPolicy=sportsEpisodeToSeries';
        } else if (imageType === 'TEAM') {
            imageUrl += '&fallbackPolicy=teamToLeague';
        }
        return imageUrl;
    };

    public buildImageUrlForChannelLogo = (baseUrl: string, logoIndex: number): string => {
        const { height: scaledHeight, width: scaledWidth } =
            DimensionScaler.getInstance().getScaledDimensions(168, 48);
        const imageUrl = `${baseUrl}/logos/${scaledWidth}x${scaledHeight}/${logoIndex - 65536}.png`;
        return imageUrl;
    };

    public buildImageUrlForHomeScreenChannelThumbnail = (
        baseUrl: string,
        logoIndex: number,
    ): string => {
        const { height: scaledHeight, width: scaledWidth } =
            DimensionScaler.getInstance().getScaledDimensions(200, 150);
        const imageUrl = `${baseUrl}/logos/${scaledWidth}x${scaledHeight}/${logoIndex - 65536}.png`;
        return imageUrl;
    };

    public buildImageUrlForJumpChannelLogo = (baseUrl: string, objectId: string): string => {
        const { height: scaledHeight, width: scaledWidth } =
            DimensionScaler.getInstance().getScaledDimensions(168, 48);
        const imageUrl = `${baseUrl}/images?objectid=${objectId}&imagetype=partnerSourceLogo&width=${scaledWidth}&height=${scaledHeight}`;
        return imageUrl;
    };

    public getImageType = (type: string): ImageType => {
        switch (type) {
            case PreviewType.MoviePreview:
                return 'MOVIE';
            case PreviewType.EpisodePreview:
                return 'EPISODE';
            case PreviewType.SportEventPreview:
                return 'SPORTS';
            default:
                return 'EPISODE';
        }
    };

    public getImageTypeFromFeedItemType = (feedItemType: FeedItemType): ImageType => {
        switch (feedItemType) {
            case FeedItemType.Movie:
                return 'MOVIE';
            case FeedItemType.SportsEvent:
                return 'SPORTS';
            case FeedItemType.SportsTeam:
            case FeedItemType.SportsOrganization:
                return 'TEAM';
            case FeedItemType.Series:
            case FeedItemType.Promotion:
                return 'COLLECTION';
            case FeedItemType.Episode:
            default:
                return 'EPISODE';
        }
    };
}
